/**
 * Public Contractor Registry directory (docs/design/contractor-registry/spec.md §3c/§4/§5) — NO
 * login. A traditional scannable list/table (never tiles): Business / Contact / Phone / Trades /
 * "Currently registered" — no dates of any kind on this surface. Renders ONLY what
 * listPublicContractors returns; the server already enforces visibility=='Public' AND
 * status=='Approved' and the fixed public field set, so this page never re-filters or widens a
 * fuller payload (render-only-what-the-API-returns). The legal disclaimer is tenant content (shown
 * verbatim here from config), never hard-coded English per CLAUDE.md's i18n-readiness rule.
 *
 * EMBED behavior (contractor-embed-chromeless-autoresize — the LV website team embeds
 * `/contractors?embed=1&chrome=off` as a direct iframe, no widget launcher): mirrors the
 * ConcernsMapPage/MeetingsPage/FaqPage chromeless pattern exactly, three parts —
 *  1. `chromeless` (plain `?embed=1&chrome=off`, derived in App.tsx) hides this page's own H1 +
 *     intro paragraph so it doesn't duplicate the host page's own heading. The "Registration
 *     notice" disclaimer stays visible either way (LV team confirmed that's fine).
 *  2. `useEmbedResize` posts `{type:"towndesk:contractors-resize", height}` to the parent on
 *     mount and whenever the observed root's rendered height changes — including a search/
 *     trade-filter narrowing the table — so the host can auto-size the iframe instead of
 *     showing an inner scrollbar.
 *  3. `useChromelessBody`-equivalent effect makes the iframe's own `<html>/<body>` transparent
 *     while chromeless (the `.td-intake--chromeless` container alone isn't enough — the body
 *     still paints the cream `--td-paper` background otherwise), reverted on unmount.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { listPublicContractors, listPublicTradeCategories } from "../api/index.js";
import type { PublicTradeCategory } from "../api/contractors.js";
import type { PublicContractorView } from "../api/types.js";
import { TownHeader } from "../components/TownHeader.js";
import { ResidentThemeRoot } from "../components/ResidentThemeRoot.js";
import { Skeleton, SkeletonRegion } from "../components/Skeleton.js";
import { useTheme } from "../theme/ThemeProvider.js";
import { useDocumentTitle, pageTitle } from "../components/useDocumentTitle.js";
import { useEmbedResize } from "../components/useEmbedResize.js";

/**
 * Verbatim per spec §7. The spec calls for this to be TENANT CONTENT (a per-tenant configurable
 * string, "so a future town can substitute its own registration-office contact and legal framing
 * without a code change") — that storage/admin-editing mechanism doesn't exist yet on
 * TenantSettings/EmbedConfig (out of scope for this PR's backend contract, PR #459). Flagging this
 * gap rather than blocking the build: this single named constant is the ONE place the string
 * lives, so wiring it to a real tenant field later is a one-line change here, not a rewrite.
 */
const DEFAULT_REGISTRATION_DISCLAIMER =
  "Contractors on this list were registered to do business within Linn Valley on the date of " +
  "registration approval — they paid the registration fee and provided documentation of current " +
  "liability insurance coverage at that time. If the liability insurance certificate on file has " +
  "expired, the contractor is no longer registered and cannot do work in Linn Valley. " +
  "Registration is not an endorsement of the contractor's work, qualifications, or experience; a " +
  "contractor's insurance policy may not cover workmanship. Questions: City Hall, 913-757-2391 / " +
  "codesclerk@linnvalleyks.com.";

type SortOrder = "name" | "recent";

interface ContractorsDirectoryPageProps {
  embed?: boolean;
  /** `?embed=1&chrome=off` only (App.tsx) — no-op when `embed` is false. Mirrors
   *  MeetingsPage/FaqPage/ConcernsMapPage's chromeless convention. */
  chromeless?: boolean;
}

export function ContractorsDirectoryPage({
  embed = false,
  chromeless = false,
}: ContractorsDirectoryPageProps): JSX.Element {
  const { config } = useTheme();
  useDocumentTitle(pageTitle("Registered Contractors", config.townName));
  const isChromeless = embed && chromeless;

  const rootRef = useRef<HTMLDivElement>(null);
  // Posts `towndesk:contractors-resize` on mount and on every content-height change (search typed,
  // trade pill clicked, window resized) — the ResizeObserver watches this root, which wraps the
  // filtered table, so a 118-row list narrowing to a handful re-posts a smaller height for free.
  useEmbedResize(embed, rootRef, "towndesk:contractors-resize");

  // CHROMELESS embed: `.td-intake--chromeless` makes our own container transparent, but the
  // iframe's own <body> still paints the cream `--td-paper` background (base.css) otherwise —
  // identical gotcha/fix as MeetingsPage/ConcernsMapPage/FaqPage. Reverted on unmount.
  useEffect(() => {
    if (!isChromeless) return;
    const { documentElement: html, body } = document;
    html.classList.add("td-embed-transparent");
    body.classList.add("td-embed-transparent");
    return () => {
      html.classList.remove("td-embed-transparent");
      body.classList.remove("td-embed-transparent");
    };
  }, [isChromeless]);

  const [params, setParams] = useSearchParams();
  const trade = params.get("trade") ?? "";
  const q = params.get("q") ?? "";
  const sort = (params.get("sort") as SortOrder) || "name";

  const [searchInput, setSearchInput] = useState(q);
  const [contractors, setContractors] = useState<PublicContractorView[] | null>(null);
  const [trades, setTrades] = useState<PublicTradeCategory[]>([]);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    setSearchInput(q);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (searchInput) next.set("q", searchInput);
      else next.delete("q");
      setParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    let live = true;
    setContractors(null);
    setLoadError(false);
    listPublicContractors(trade || undefined, q || undefined)
      .then((rows) => {
        if (live) setContractors(rows);
      })
      .catch(() => {
        if (live) setLoadError(true);
      });
    return () => {
      live = false;
    };
  }, [trade, q]);

  // Trade filter pills are drawn from the tenant's full trade taxonomy (not derived from the
  // current result set) so a trade with zero currently-registered contractors still shows up as a
  // selectable (if empty) filter — same "stable unfiltered list for filter options" convention
  // ConcernsMapPage uses for its category dropdown. MUST use the PUBLIC trades endpoint (no auth)
  // — the staff Kind=trade Categories endpoint 401s here, which was silently swallowed by the
  // .catch below and was why both the per-trade pills AND the trade labels on each row (resolved
  // via this same `trades` list) were missing/raw-id on prod.
  useEffect(() => {
    listPublicTradeCategories().then(setTrades).catch(() => undefined);
  }, []);

  const sorted = useMemo(() => {
    if (!contractors) return [];
    const list = [...contractors];
    if (sort === "name") list.sort((a, b) => a.businessName.localeCompare(b.businessName));
    // "Recently added" — the server returns newest-first already has no stable ordering guarantee
    // documented; without a createdAt on the public shape (deliberately omitted, spec §4), the best
    // available proxy is the server's own response order, left untouched here.
    return list;
  }, [contractors, sort]);

  function setParam(key: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  const disclaimer = DEFAULT_REGISTRATION_DISCLAIMER;

  return (
    <ResidentThemeRoot
      ref={rootRef}
      className={`td-intake${embed ? " td-intake--embed td-intake--embed-wide" : " td-intake--wide"}${isChromeless ? " td-intake--chromeless" : ""}`}
    >
      {isChromeless ? null : (
        <a href="#td-contractors-main" className="td-skip-link">
          Skip to registered contractors
        </a>
      )}
      <TownHeader embed={embed} subcopy={`Registered contractors in ${config.townName}.`} />

      <main id="td-contractors-main" className="td-intake__main td-cr-directory">
        {isChromeless ? null : (
          <div className="td-pagehead">
            <div className="td-pagehead__text">
              <h1 className="td-pagehead__title">Registered contractors</h1>
              <p className="td-pagehead__desc">
                Businesses currently registered to do work in {config.townName}. Browse the full
                list, filter by trade, or search a business name — no account needed.
              </p>
            </div>
          </div>
        )}

        <p className="td-cr-disclaimer">
          <strong>Registration notice:</strong> {disclaimer}
        </p>

        <form className="td-cr-search" role="search" aria-label="Search registered contractors">
          <label htmlFor="cr-search-input" className="td-sr-only">
            Search by business name
          </label>
          <input
            id="cr-search-input"
            className="td-input"
            type="search"
            placeholder="Search by business name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {q ? (
            <button
              type="button"
              className="td-btn td-btn--secondary"
              onClick={() => {
                setSearchInput("");
                setParam("q", "");
              }}
            >
              Clear
            </button>
          ) : null}
        </form>

        <div className="td-cr-controls">
          <div className="td-cr-tradepills" role="group" aria-label="Filter by trade">
            <button
              type="button"
              className={`td-cr-tradepill${!trade ? " is-active" : ""}`}
              aria-pressed={!trade}
              onClick={() => setParam("trade", "")}
            >
              All trades
            </button>
            {trades.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`td-cr-tradepill${trade === t.id ? " is-active" : ""}`}
                aria-pressed={trade === t.id}
                onClick={() => setParam("trade", trade === t.id ? "" : t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <label className="td-cr-sort">
            Sort
            <select
              className="td-select"
              value={sort}
              onChange={(e) => setParam("sort", e.target.value === "name" ? "" : e.target.value)}
            >
              <option value="name">Name (A–Z)</option>
              <option value="recent">Recently added</option>
            </select>
          </label>
        </div>

        <p className="td-cr-count" aria-live="polite">
          {contractors === null
            ? "Loading…"
            : `${sorted.length} registered contractor${sorted.length === 1 ? "" : "s"}`}
        </p>

        {loadError ? (
          <div className="td-alert td-alert--danger" role="alert">
            Couldn't load the contractor directory — please try again.
          </div>
        ) : contractors === null ? (
          <ContractorsDirectorySkeleton />
        ) : sorted.length === 0 ? (
          <p className="td-muted">
            No registered contractor currently listed
            {q ? ` for "${q}"` : trade ? ` for this trade` : ""}. Try a different search or trade,
            or check back later.
          </p>
        ) : (
          <div className="td-cr-tablewrap">
            <table className="td-cr-table">
              <caption className="td-sr-only">
                Registered contractors — business, contact, phone, trades, and registration status
              </caption>
              <thead>
                <tr>
                  <th scope="col">Business</th>
                  <th scope="col">Contact</th>
                  <th scope="col">Phone</th>
                  <th scope="col">Trades</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((c) => (
                  <tr key={c.id}>
                    <td className="td-cr-row__biz">{c.businessName}</td>
                    <td>{c.contactName}</td>
                    <td className="td-cr-row__phone">{c.phone}</td>
                    <td>
                      <div className="td-cr-trades">
                        {c.trades.map((t) => (
                          <span key={t} className="td-cr-trade-chip">
                            {trades.find((tc) => tc.id === t)?.label ?? t}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className="td-vis td-vis--public">Active</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="td-cr-disclaimer td-cr-disclaimer--footer">
          Each listing shows the business name, contact person, phone, and trades on file with the
          city — it's a referral directory, so contact details are shown by design. Being on this
          list means the registration is currently active; license/insurance dates, carrier, policy
          #, bond details, email on file, and internal staff notes stay private to City staff. Only
          contractors marked Public and currently Approved (active insurance certificate on file)
          appear here — once a certificate lapses, the listing is removed automatically.
        </p>
      </main>

      <footer className="td-footer">
        <span>
          Powered by{" "}
          <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
            TownDesk
          </a>
        </span>
      </footer>
    </ResidentThemeRoot>
  );
}

function ContractorsDirectorySkeleton(): JSX.Element {
  return (
    <SkeletonRegion label="Loading registered contractors…" className="td-skel-rows">
      {Array.from({ length: 5 }).map((_, i) => (
        <div className="td-skel-row" key={i}>
          <Skeleton width="55%" />
          <Skeleton width="30%" height="var(--td-text-sm)" />
        </div>
      ))}
    </SkeletonRegion>
  );
}
